// src/pages/Wallet/AddWallet.js
import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaArrowLeft, FaPlusCircle, FaCheckCircle } from "react-icons/fa";
import { updateWalletBalance } from "../../../services/Wallet/AddWallet";
import MinAmountModal from "./MinAmountModal"; // ✅ import modal

export default function AddWallet({ wallet, onBack, customerId }) {
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [currentBalance, setCurrentBalance] = useState(wallet?.currentBalance || 0);
  const [showMinModal, setShowMinModal] = useState(false); // ✅ modal state

  const quickAmounts = [100, 200, 500, 1000];

  useEffect(() => {
    setCurrentBalance(wallet?.currentBalance || 0);
  }, [wallet]);

  const handleAddAmount = async () => {
    if (loading) return;

    const numericAmount = Number(amount);

    // ✅ Check if amount < 500
    if (!numericAmount || numericAmount < 500) {
      setShowMinModal(true);
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await updateWalletBalance(customerId, numericAmount);
      console.log("Wallet API Response:", res);

      if (res.ok && res.data?.succeeded) {
        setStatus("success");
        setMessage(res.data.message || `₹${numericAmount} added successfully!`);

        const updatedBalance = res.data?.data?.currentBalance || wallet?.currentBalance || 0;
        setCurrentBalance(Number(updatedBalance));
        setAmount("");
      } else {
        setStatus("error");
        setMessage(res.data?.message || "Failed to add amount.");
      }
    } catch (error) {
      console.error(error);
      setStatus("error");
      setMessage("An error occurred while adding amount.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-4">
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <button className="btn btn-outline-danger rounded-circle me-3" onClick={onBack}>
          <FaArrowLeft />
        </button>
        <h4 className="fw-bold text-danger mb-0">Add Money to Wallet</h4>
      </div>

      {/* Wallet Card */}
      <div
        className="card border-0 shadow-lg p-4 mx-auto animate__animated animate__fadeIn"
        style={{
          maxWidth: "480px",
          borderRadius: "20px",
          background: "linear-gradient(145deg, #ffebeb, #ffffff)",
        }}
      >
        <div className="text-center mb-4">
          <FaPlusCircle className="text-danger" size={45} />
          <h5 className="fw-bold mt-3 text-secondary">
            Current Balance: <span className="text-dark">₹{currentBalance.toFixed(2)}</span>
          </h5>
        </div>

        {/* Quick Amount Buttons */}
        <div className="mb-3">
          <label className="form-label fw-semibold text-secondary">Quick Add</label>
          <div className="d-flex flex-wrap gap-2">
            {quickAmounts.map((amt) => (
              <button
                key={amt}
                type="button"
                className={`btn fw-semibold px-3 py-2 ${
                  amount == amt ? "btn-danger text-white" : "btn-outline-danger"
                }`}
                onClick={() => setAmount(amt)}
                disabled={loading}
              >
                ₹{amt}
              </button>
            ))}
          </div>
        </div>

        {/* Amount Input */}
        <div className="mb-3 mt-2">
          <label className="form-label fw-semibold text-secondary">Enter Amount (₹)</label>
          <input
            type="number"
            className="form-control shadow-sm rounded"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter custom amount"
            min="1"
            disabled={loading}
            style={{ fontSize: "18px", padding: "12px" }}
          />
        </div>

        {/* Add Amount Button */}
        <button
          type="button"
          className="btn btn-danger w-100 fw-semibold py-2 shadow-sm"
          onClick={handleAddAmount}
          disabled={loading}
          style={{ fontSize: "18px" }}
        >
          {loading ? "Processing..." : "Add Money"}
        </button>

        {/* Status Message */}
        {message && (
          <div
            className={`alert mt-3 text-center ${
              status === "success" ? "alert-success" : "alert-warning"
            }`}
          >
            {status === "success" && <FaCheckCircle className="me-2" />}
            {message}
          </div>
        )}

        {/* Back Button - Only after success */}
        {status === "success" && (
          <button className="btn btn-outline-danger w-100 fw-semibold mt-3" onClick={onBack}>
            Back to Wallet
          </button>
        )}
      </div>

      {/* ✅ Min Amount Modal */}
      <MinAmountModal
        show={showMinModal}
        onClose={() => setShowMinModal(false)}
        minAmount={500}
      />
    </div>
  );
}